/**
 * WebSocket client with reconnect.
 *
 * The connection is treated as disposable: nothing is buffered here, because
 * every message is already durable on the server until acked. Reconnecting
 * simply re-drains the queue.
 */
import type { Envelope } from '@vav/protocol';

export type ConnectionStatus = 'offline' | 'connecting' | 'online';

export interface SocketHandlers {
  onEnvelope(envelope: Envelope): void;
  onQueueDrained(): void;
  onTyping(event: { userId: string; conversationId: string; isTyping: boolean }): void;
  onPresence(event: { userId: string; online: boolean; lastSeen?: number }): void;
  onStatus(status: ConnectionStatus): void;
}

/** Injected so React Native's global WebSocket works unchanged. */
export type WebSocketFactory = (url: string) => WebSocket;

const BASE_RETRY_MS = 500;
const MAX_RETRY_MS = 30_000;

export class SocketClient {
  #url: string;
  #token: string;
  #handlers: SocketHandlers;
  #factory: WebSocketFactory;
  #socket?: WebSocket;
  #retries = 0;
  #timer?: ReturnType<typeof setTimeout>;
  #closed = false;
  #watching: string[] = [];
  #status: ConnectionStatus = 'offline';

  constructor(
    url: string,
    token: string,
    handlers: SocketHandlers,
    factory: WebSocketFactory = (u) => new WebSocket(u),
  ) {
    this.#url = url;
    this.#token = token;
    this.#handlers = handlers;
    this.#factory = factory;
  }

  get status(): ConnectionStatus {
    return this.#status;
  }

  #setStatus(status: ConnectionStatus): void {
    if (this.#status === status) return;
    this.#status = status;
    this.#handlers.onStatus(status);
  }

  connect(): void {
    if (this.#closed) return;
    if (this.#socket && this.#socket.readyState <= WebSocket.OPEN) return;

    this.#setStatus('connecting');
    const socket = this.#factory(`${this.#url}/ws?token=${encodeURIComponent(this.#token)}`);
    this.#socket = socket;

    socket.onopen = () => {
      this.#retries = 0;
      this.#setStatus('online');
      if (this.#watching.length) this.send({ t: 'watch', userIds: this.#watching });
    };

    socket.onmessage = (event) => {
      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(String(event.data));
      } catch {
        return;
      }
      switch (parsed.t) {
        case 'envelope':
          this.#handlers.onEnvelope(parsed.envelope as Envelope);
          break;
        case 'queue-drained':
          this.#handlers.onQueueDrained();
          break;
        case 'typing':
          this.#handlers.onTyping(parsed as never);
          break;
        case 'presence':
          this.#handlers.onPresence(parsed as never);
          break;
        default:
          break;
      }
    };

    socket.onclose = () => {
      this.#setStatus('offline');
      this.#scheduleReconnect();
    };
    socket.onerror = () => socket.close();
  }

  /**
   * Exponential backoff with jitter. The jitter matters: without it, a server
   * restart makes every client reconnect in the same millisecond and knock it
   * over again.
   */
  #scheduleReconnect(): void {
    if (this.#closed || this.#timer) return;
    const backoff = Math.min(BASE_RETRY_MS * 2 ** this.#retries, MAX_RETRY_MS);
    const delay = backoff / 2 + Math.random() * (backoff / 2);
    this.#retries += 1;
    this.#timer = setTimeout(() => {
      this.#timer = undefined;
      this.connect();
    }, delay);
  }

  send(event: unknown): void {
    if (this.#socket?.readyState === WebSocket.OPEN) this.#socket.send(JSON.stringify(event));
  }

  ack(ids: string[]): void {
    if (ids.length) this.send({ t: 'ack', ids });
  }

  setTyping(conversationId: string, recipients: string[], isTyping: boolean): void {
    this.send({ t: 'typing', conversationId, recipients, isTyping });
  }

  watch(userIds: string[]): void {
    this.#watching = userIds;
    this.send({ t: 'watch', userIds });
  }

  close(): void {
    this.#closed = true;
    if (this.#timer) clearTimeout(this.#timer);
    this.#socket?.close();
    this.#setStatus('offline');
  }
}
